/**
 * Toast Service Utilities
 *
 * Provides a standardized way to show toast notifications from sagas.
 * Uses the toast function from @spcc/uikit which works without React context.
 *
 * @example
 * // In a saga:
 * import { showSuccessToast, showErrorToast } from '@spcc/core'
 *
 * function* createOrderSaga(action) {
 *   try {
 *     const order = yield call(api.createOrder, action.payload)
 *     yield put(ordersActions.createOrderSuccess(order))
 *     showSuccessToast('orders.created', 'orders.createdDescription')
 *   } catch (error) {
 *     yield put(ordersActions.createOrderFailure(extractErrorMessage(error)))
 *     showErrorToast('orders.createFailed', extractErrorMessage(error))
 *   }
 * }
 */

// Re-export toast from uikit for direct usage
// Note: This is a side-effect import - the toast function works globally
// The Toaster component must be mounted in the app for toasts to display

// ============================================================================
// Types
// ============================================================================

export type ToastVariant = 'default' | 'destructive'

export interface ToastOptions {
  /** Title of the toast (i18n key or string) */
  title?: string
  /** Description/body of the toast (i18n key or string) */
  description?: string
  /** Variant - 'default' for success/info, 'destructive' for errors */
  variant?: ToastVariant
  /** Duration in milliseconds (default: 5000 for success, 7000 for errors) */
  duration?: number
  /** Action element (button) */
  action?: React.ReactNode
  /** Additional CSS classes for the toast container */
  className?: string
}

// ============================================================================
// Toast Duration Constants
// ============================================================================

export const TOAST_DURATION = {
  /** Quick success messages */
  SHORT: 3000,
  /** Standard success messages */
  DEFAULT: 5000,
  /** Error messages (need more time to read) */
  LONG: 5000,
  /** Critical errors (stay until dismissed) */
  PERSISTENT: Infinity,
} as const

// ============================================================================
// Toast Service
// ============================================================================

// We use a getter pattern to avoid circular imports and ensure
// the toast function is available when called
let toastFn: ((options: ToastOptions) => void) | null = null

/**
 * Initialize the toast service with the toast function from uikit
 * This should be called once in the app initialization
 */
export function initializeToastService(toast: (options: ToastOptions) => void): void {
  toastFn = toast
}

/**
 * Get the toast function, with a fallback console warning if not initialized
 */
function getToast(): (options: ToastOptions) => void {
  if (!toastFn) {
    // Try dynamic import as fallback
    console.warn(
      '[Toast] Toast service not initialized. Call initializeToastService() in your app initialization.'
    )
    return () => {} // No-op if not initialized
  }
  return toastFn
}

// ============================================================================
// Toast Helper Functions
// ============================================================================

/**
 * Show a success toast
 *
 * @param title - Title (i18n key recommended)
 * @param description - Optional description
 * @param duration - Duration in ms (default: 5000)
 */
export function showSuccessToast(
  title: string,
  description?: string,
  duration: number = TOAST_DURATION.DEFAULT
): void {
  getToast()({
    title,
    description,
    variant: 'default',
    duration,
  })
}

/**
 * Show an error toast
 *
 * @param title - Title (i18n key recommended)
 * @param description - Error message or description
 * @param duration - Duration in ms (default: 7000)
 */
export function showErrorToast(
  title: string,
  description?: string,
  duration: number = TOAST_DURATION.LONG
): void {
  getToast()({
    title,
    description,
    variant: 'destructive',
    duration,
  })
}

/**
 * Show an info toast (same as success but for informational messages)
 *
 * @param title - Title (i18n key recommended)
 * @param description - Optional description
 * @param duration - Duration in ms (default: 5000)
 */
export function showInfoToast(
  title: string,
  description?: string,
  duration: number = TOAST_DURATION.DEFAULT
): void {
  getToast()({
    title,
    description,
    variant: 'default',
    duration,
  })
}

/**
 * Show a warning toast (uses destructive variant but with warning semantics)
 *
 * @param title - Title (i18n key recommended)
 * @param description - Optional description
 * @param duration - Duration in ms (default: 7000)
 */
export function showWarningToast(
  title: string,
  description?: string,
  duration: number = TOAST_DURATION.LONG
): void {
  getToast()({
    title,
    description,
    variant: 'destructive',
    duration,
  })
}

/**
 * Show a custom toast with full control
 *
 * @param options - Full toast options
 */
export function showToast(options: ToastOptions): void {
  getToast()(options)
}

// ============================================================================
// Saga Toast Helpers
// ============================================================================

/**
 * Create toast message creators for a specific domain
 * This provides type-safe, consistent toast messages for CRUD operations
 *
 * @param domain - Domain name for i18n keys (e.g., 'addresses', 'orders')
 * @returns Object with toast helper functions for the domain
 *
 * @example
 * const addressToasts = createDomainToasts('addresses')
 *
 * // In saga:
 * addressToasts.created()           // "Address created successfully"
 * addressToasts.updated()           // "Address updated successfully"
 * addressToasts.deleted()           // "Address deleted successfully"
 * addressToasts.error('create', errorMessage) // "Failed to create address: ..."
 */
export function createDomainToasts(domain: string) {
  return {
    /** Show toast for successful creation */
    created(entityName?: string): void {
      showSuccessToast(
        'toast.success',
        `toast.${domain}.created`
      )
    },

    /** Show toast for successful update */
    updated(entityName?: string): void {
      showSuccessToast(
        'toast.success',
        `toast.${domain}.updated`
      )
    },

    /** Show toast for successful deletion */
    deleted(entityName?: string): void {
      showSuccessToast(
        'toast.success',
        `toast.${domain}.deleted`
      )
    },

    /** Show toast for successful save (create or update) */
    saved(entityName?: string): void {
      showSuccessToast(
        'toast.success',
        `toast.${domain}.saved`
      )
    },

    /** Show toast for operation error */
    error(operation: 'create' | 'update' | 'delete' | 'fetch' | 'save', errorMessage: string): void {
      showErrorToast(
        'toast.error',
        errorMessage
      )
    },

    /** Show toast for fetch error (usually less prominent) */
    fetchError(errorMessage: string): void {
      showErrorToast(
        'toast.error',
        errorMessage
      )
    },

    /** Show custom success toast */
    success(descriptionKey: string): void {
      showSuccessToast('toast.success', descriptionKey)
    },

    /** Show custom error toast */
    customError(descriptionKey: string): void {
      showErrorToast('toast.error', descriptionKey)
    },
  }
}
